// Which folded rows the reader opened, remembered per table for the browser session (design system 03-components §13:
// "Expansion state SHOULD persist within a session where the list is long"; 04-data §1.7 *Folding*; the owner's ruling
// Q19 of 2026-10-06, "Remember opened folds"; Phase 1c finding G11). Opened rows were component state alone: they
// survived a sort, never a navigation, so a reader who opened three to-dos' folded values, followed one to its meeting
// and came Back found every row closed. `Collapsible.tsx` already keeps a section's open state the same way.
//
// Why sessionStorage: §13 asks for the session, and a new session starts with every row closed, as 04-data §7.1 wants of
// transient state ("which rows were expanded" is never part of a saved view). Why keyed by the page's path and the
// table: two tables on one page (Admin → Queue's three) and the same table on two records (a meeting's to-dos) are
// different lists. Why bounded: the row keys are ids, and a reader who opens rows all afternoon must not fill the store.

/** The part of the Web Storage interface used here; null where storage is absent or refused. */
export type SessionStore = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

export const FOLD_MEMORY_PREFIX = 'calis:fold-open:';
/** At most this many opened rows are remembered per table; the oldest opened go first. */
export const FOLD_MEMORY_MAX_ROWS = 200;

/** The browser session's store, or null — under node in the tests, in a private window, with site data blocked. */
export function sessionStore(): SessionStore | null {
  try {
    return typeof window === 'undefined' ? null : window.sessionStorage;
  } catch {
    return null;
  }
}

/** One table's key: the page's path and the table's own name (its sort namespace, else its caption). */
export function foldMemoryKey(pathname: string, table: string): string {
  return `${FOLD_MEMORY_PREFIX}${pathname}|${table}`;
}

/** The rows remembered open under `key`; an empty set when nothing is, or what is stored cannot be read. */
export function readOpenRows(store: SessionStore | null, key: string): ReadonlySet<string> {
  if (!store) return new Set();
  try {
    const stored = store.getItem(key);
    if (stored === null) return new Set();
    const parsed: unknown = JSON.parse(stored);
    if (!Array.isArray(parsed)) return new Set();
    return new Set(parsed.filter((entry): entry is string => typeof entry === 'string').slice(-FOLD_MEMORY_MAX_ROWS));
  } catch {
    return new Set();
  }
}

/** Remembers `rows` open under `key` — the most recently opened `FOLD_MEMORY_MAX_ROWS` — and forgets the key when none are. */
export function writeOpenRows(store: SessionStore | null, key: string, rows: ReadonlySet<string>): void {
  if (!store) return;
  try {
    if (rows.size === 0) store.removeItem(key);
    else store.setItem(key, JSON.stringify([...rows].slice(-FOLD_MEMORY_MAX_ROWS)));
  } catch {
    // Not remembered: the row still opens and closes on this page.
  }
}
